import { ReactElement, useState } from 'react';
import toast from 'react-hot-toast';
import RadioOption from '../../../../components/RadioOption';
import OptionSection from './OptionSection';
import {
  asSessionDataSource,
  SESSION_DATA_SOURCE_OPTIONS,
  SessionDataSource,
  sessionDataSourceName,
} from '../../../../types/Session';

interface DataSourceSectionProps {
  session: any;
  updateSession: any;
  invalidateSession: () => Promise<void>;
}

/**
 * Session-level data source for device runs — one setting, read from the
 * run's own session every time a run is processed, so it applies to every
 * run in the session, existing ones included, with nothing pushed down.
 *
 * **Stream**: the episode stream Nimbus uploads live (the run is a time
 * window cut out of it, cached per run) is the source of record. Runs
 * appear in the run list at run start, and the device's own zip upload is
 * only the last resort when the stream holds nothing for a run.
 *
 * **Device zip** (default, the legacy order): the device's zip upload is
 * read first; the stream stands in when the zip is missing.
 *
 * Whichever source a run was actually processed from is shown on the run
 * (`source_used`), with a health warning when it was the fallback.
 */
export default function DataSourceSection({
  session,
  updateSession,
  invalidateSession,
}: DataSourceSectionProps): ReactElement {
  const [isUpdating, setIsUpdating] = useState(false);
  const dataSource = asSessionDataSource(session?.data_source);

  const handleChange = async (next: SessionDataSource) => {
    if (isUpdating || next === dataSource) return;
    try {
      setIsUpdating(true);
      await updateSession.mutateAsync({ data_source: next });
      toast.success(`Data source set to ${sessionDataSourceName(next)}`);
      invalidateSession();
    } catch (e) {
      console.error(e);
      toast.error('Error updating data source');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <OptionSection
      title="Data Source"
      status={
        <span className="cr-text-sm cr-text-gray-500">{sessionDataSourceName(dataSource)}</span>
      }
    >
      <div className="cr-mb-4 cr-flex cr-flex-col cr-gap-2 cr-rounded-lg cr-bg-white cr-p-4 cr-shadow-md">
        {SESSION_DATA_SOURCE_OPTIONS.map(({ value, label, hint }) => (
          <RadioOption
            key={value}
            name="sessionDataSource"
            value={value}
            checked={dataSource === value}
            onChange={() => handleChange(value)}
            disabled={isUpdating}
            label={label}
            hint={hint}
          />
        ))}
        <p className="cr-text-xs cr-text-gray-500">
          Applies to every run in this session, existing runs included, the next time it is processed. Which
          source a run was actually read from is shown on the run.
        </p>
      </div>
    </OptionSection>
  );
}
